/**
 * Order Utilities - Used by global order handler and MultiSelect
 */

export const swapArrayElements = <T>(arr: T[], indexA: number, indexB: number): boolean => {
    if (indexB < 0 || indexB >= arr.length) {
        return false;
    }

    [arr[indexA], arr[indexB]] = [arr[indexB], arr[indexA]];
    return true;
};

export const moveItemUp = (item: HTMLElement): boolean => {
    const prev = item.previousElementSibling;

    if (prev && item.parentNode) {
        item.parentNode.insertBefore(item, prev);

        return true;
    }

    return false;
};

export const moveItemDown = (item: HTMLElement): boolean => {
    const next = item.nextElementSibling;

    if (next && item.parentNode) {
        item.parentNode.insertBefore(next, item);

        return true;
    }

    return false;
};

export const getOrder = (
    container: HTMLElement,
    itemSelector: string = '[data-id]',
    idAttr: string = 'id'
): string[] => {
    return Array.from(container.querySelectorAll<HTMLElement>(itemSelector))
        .map(el => el.dataset[idAttr])
        .filter((id): id is string => id !== undefined);
};

export const reorderContainer = (
    container: HTMLElement,
    order: string[],
    idAttr: string = 'id'
): void => {
    const children = Array.from(container.children) as HTMLElement[];

    children
        .sort((a, b) => order.indexOf(a.dataset[idAttr] || '') - order.indexOf(b.dataset[idAttr] || ''))
        .forEach(child => container.appendChild(child));
};

interface OrderButtonsOptions {
    itemSelector?: string;
    upSelector?: string;
    downSelector?: string;
    onOrderChange?: ((order: (string | undefined)[], item: HTMLElement) => void) | null;
}

export const initOrderButtons = (container: HTMLElement, options: OrderButtonsOptions = {}): void => {
    const {
        itemSelector = '[data-id]',
        upSelector = '.js-order-up',
        downSelector = '.js-order-down',
        onOrderChange = null
    } = options;

    container.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const upBtn = target.closest(upSelector);
        const downBtn = target.closest(downSelector);

        if (!upBtn && !downBtn) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();

        const item = (upBtn || downBtn)!.closest<HTMLElement>(itemSelector);

        if (!item) {
            return;
        }

        const moved = upBtn ? moveItemUp(item) : moveItemDown(item);

        if (moved && onOrderChange) {
            const order = getOrder(container, itemSelector);
            onOrderChange(order, item);
        }
    });
};

export default {
    swapArrayElements,
    moveItemUp,
    moveItemDown,
    getOrder,
    reorderContainer,
    initOrderButtons
};